import React, { useState } from 'react';
import { arrayOf, bool, number, shape, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Avatar } from '../../components';

import css from './RsvpAvatarStack.module.css';

/**
 * Who has reserved a spot for an episode: "+20 · 23 going".
 *
 * Who may see faces is decided by the server (see server/api-util/episodeRsvp.js), not
 * re-judged here: `canSeeIdentities` is true only for the host (the listing's author) and staff
 * (organization members who can publish episodes). Everyone else gets the total on its own, with
 * no faces and no names. This is what keeps an org looking at a rented Gig Boost episode from
 * reading the creator's audience out of a calendar pop-up.
 *
 * There is no "declined" state to leak: an RSVP is a toggle, so the list is by
 * construction only the people who said yes.
 */

// Below this many RSVPs the stack is count-only, even for the host. One or
// two faces read as "only these people are coming", which discourages exactly
// the behaviour the stack exists to encourage. Agreed with Yuko, 1 Sep 2026.
export const FACES_FROM = 3;

/**
 * The faces rule: faces only for the host and staff, and only from 3 RSVPs. Below 3, or for
 * anyone else, the count shows without faces.
 */
export const showsRsvpFaces = ({ total = 0, canSeeIdentities = false, attendeeCount = 0 }) =>
  !!canSeeIdentities && attendeeCount > 0 && total >= FACES_FROM;

const initialsOf = name => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '';
  return parts.length === 1
    ? parts[0].slice(0, 1).toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// An attendee as the user shape Avatar reads (the server sends only these three fields).
const attendeeAsUser = ({ userId, displayName, imageUrl }) => ({
  id: userId ? { uuid: userId } : null,
  type: 'user',
  attributes: {
    profile: {
      displayName: displayName || '',
      abbreviatedName: initialsOf(displayName),
      publicData: imageUrl ? { avatarImage: { link: imageUrl } } : {},
    },
  },
});

// Where space is tight the stack shows fewer faces (spec table, D).
const MOBILE_AVATAR_LIMIT = 3;
const isNarrow = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 767px)').matches;

const RsvpAvatarStack = props => {
  const { className, total = 0, canSeeIdentities = false, attendees = [], avatarLimit = 5 } = props;
  const intl = useIntl();
  const [listOpen, setListOpen] = useState(false);
  const [limit] = useState(() =>
    isNarrow() ? Math.min(MOBILE_AVATAR_LIMIT, avatarLimit) : avatarLimit
  );
  const classes = classNames(css.root, className);

  // Nobody has reserved a spot yet. Say so rather than rendering an empty rail
  // that reads as a loading state that never resolves.
  if (!total) {
    return (
      <div className={classes}>
        <span className={css.label}>
          <FormattedMessage id="RsvpAvatarStack.noneYet" />
        </span>
      </div>
    );
  }

  const countLabel = (
    <span className={css.label}>
      <FormattedMessage
        id="RsvpAvatarStack.going"
        values={{ count: total, b: chunks => <b className={css.count}>{chunks}</b> }}
      />
    </span>
  );

  if (!showsRsvpFaces({ total, canSeeIdentities, attendeeCount: attendees.length })) {
    return <div className={classes}>{countLabel}</div>;
  }

  const shown = attendees.slice(0, limit);
  // Overflow is measured against the real total, not the fetched slice, so a
  // capped lookup can never understate how many people are coming.
  const remaining = total - shown.length;

  return (
    <div className={classes}>
      {/* The whole stack opens the name list, so it works from three faces
          on, not only when there is a "+N", and from the keyboard. */}
      <button
        type="button"
        className={css.stack}
        onClick={() => setListOpen(open => !open)}
        aria-expanded={listOpen}
        aria-label={intl.formatMessage({ id: 'RsvpAvatarStack.showNames' }, { count: total })}
      >
        {shown.map((attendee, index) => (
          <Avatar
            key={attendee.userId || index}
            className={css.face}
            size={30}
            user={attendeeAsUser(attendee)}
            disableProfileLink
          />
        ))}

        {remaining > 0 ? (
          <span className={classNames(css.face, css.overflow)}>
            <FormattedMessage id="RsvpAvatarStack.overflow" values={{ count: remaining }} />
          </span>
        ) : null}
      </button>

      {countLabel}

      {listOpen ? (
        <ul className={css.nameList}>
          {attendees.map((attendee, index) => (
            <li key={attendee.userId || index} className={css.nameListItem}>
              {attendee.displayName || <FormattedMessage id="RsvpAvatarStack.unnamedAttendee" />}
            </li>
          ))}
          {/* The lookup is capped, so be explicit when the list is partial
              rather than letting it look like the whole guest list. */}
          {total > attendees.length ? (
            <li className={classNames(css.nameListItem, css.nameListMore)}>
              <FormattedMessage
                id="RsvpAvatarStack.andMore"
                values={{ count: total - attendees.length }}
              />
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
};

RsvpAvatarStack.propTypes = {
  className: string,
  total: number,
  // Set by the server for the host and staff only
  canSeeIdentities: bool,
  attendees: arrayOf(
    shape({
      userId: string,
      displayName: string,
      imageUrl: string,
    })
  ),
  avatarLimit: number,
};

export default RsvpAvatarStack;
